import React from "react";
import axios from "axios";

import {
  API_get_learning_file,
  API_get_learning_swipe_deck_for_employee,
  API_get_learning_swipe_deck_detail,
  API_post_learning_swipe_card_response,
  API_post_learning_swipe_deck_completion,
  API_post_public_swipe_card_submit,
} from "../../../../constants";
import {
  getRightworkAPI,
  postRightworkAPI,
} from "../../requests/utilities/requests";

const SIGNED_MEDIA_REFRESH_AFTER_MS = 45 * 60 * 1000;

/**
 * Data + mutators for the swipe deck player.
 *
 * On mount, fetches the deck (cards, cover, accent), the user's existing
 * card responses for this section embedding, and the deck completion
 * record (if any). The player uses existing responses to pre-fill answered
 * interactive cards on resume.
 *
 * `employeeID` is the store_team_member id and is only forwarded when set
 * (manager-on-behalf-of-employee flow). Matches the convention in
 * CompleteQuizModal / Signature / DocumentUpload.
 *
 * `previewMode` flips the hook into admin-preview mode:
 *   - If `previewDeck` is provided, the deck is used directly (no fetch)
 *   - Otherwise the deck is fetched via the admin detail endpoint, which
 *     bypasses the section-embedding check and includes quiz answer keys
 *   - `submitResponse` computes `is_correct` locally and never networks
 *   - `submitCompletion` is a local stub
 *   - `sectionUuid` becomes optional
 *
 * The admin endpoint includes the `is_correct` flag on quiz choices, so
 * preview can mirror real player behavior (correct/incorrect feedback,
 * completion stats) without polluting the admin's response history.
 */
export function useSwipeDeck({
  deckUuid,
  sectionUuid,
  companyId,
  employeeID,
  language,
  previewMode = false,
  previewDeck = null,
  // Public, unauthenticated, untracked render. When set, the deck is supplied
  // via `previewDeck` (fetched by PublicSwipeDeckView with no auth) and card
  // submissions go to the public endpoint with this token. Like preview, it
  // never writes per-user completion/response rows or refreshes media.
  publicShareToken = null,
}) {
  const isPublic = !!publicShareToken;
  // Treat public mode like preview for the no-tracking behaviors (deck seeding,
  // completion stub, no media refresh). Submissions differ (see submitResponse).
  const nonTracking = previewMode || isPublic;

  // Seed state synchronously when a `previewDeck` is supplied so the very first
  // render shows the player content — no "Loading..." flash. (Both the editor
  // preview and the public page pass a deck object directly.)
  const hasSeedDeck = nonTracking && !!previewDeck;
  const [deck, setDeck] = React.useState(() =>
    hasSeedDeck ? previewDeck : null,
  );
  const [responsesByCardUuid, setResponsesByCardUuid] = React.useState({});
  const [completion, setCompletion] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(() => !hasSeedDeck);
  const [error, setError] = React.useState(null);

  // Anchored on first mount. Reported to the server in submitCompletion
  // so reporting can show how long employees spent on each deck.
  const startedAtRef = React.useRef(Date.now());
  const mediaUrlFetchedAtByCardRef = React.useRef({});
  const mediaRefreshInFlightByCardRef = React.useRef({});

  const markDeckMediaUrlsFresh = React.useCallback((nextDeck) => {
    const now = Date.now();
    const timestamps = {};
    for (const card of nextDeck?.cards || []) {
      if (card?.media?.uuid || card?.thumbnail?.uuid) {
        timestamps[card.uuid] = now;
      }
    }
    mediaUrlFetchedAtByCardRef.current = timestamps;
  }, []);

  const fetchDeck = React.useCallback(async () => {
    // Deck object supplied directly (editor preview or public page) — skip the
    // network entirely.
    if (nonTracking && previewDeck) {
      setDeck(previewDeck);
      mediaUrlFetchedAtByCardRef.current = {};
      setResponsesByCardUuid({});
      setCompletion(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Preview-from-dashboard: fetch via the admin endpoint (no section
    // embedding required, includes answer keys for local correctness).
    if (previewMode) {
      if (!deckUuid || !companyId) return;
      setIsLoading(true);
      setError(null);
      try {
        const params = {
          company_id: companyId,
          learning_swipe_deck_uuid: deckUuid,
        };
        if (language) params.language = language;
        const res = await getRightworkAPI(
          API_get_learning_swipe_deck_detail,
          params,
        );
        const nextDeck = res.data?.redux?.payload?.learning_swipe_deck || null;
        setDeck(nextDeck);
        markDeckMediaUrlsFresh(nextDeck);
        setResponsesByCardUuid({});
        setCompletion(null);
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Normal employee path
    if (!deckUuid || !sectionUuid || !companyId) return;
    setIsLoading(true);
    setError(null);
    try {
      const params = {
        company_id: companyId,
        learning_swipe_deck_uuid: deckUuid,
        section_uuid: sectionUuid,
      };
      if (language) params.language = language;
      if (employeeID) params.store_team_member_id = employeeID;

      const res = await getRightworkAPI(
        API_get_learning_swipe_deck_for_employee,
        params,
      );
      const payload = res.data?.redux?.payload || {};
      const nextDeck = payload.learning_swipe_deck || null;

      setDeck(nextDeck);
      markDeckMediaUrlsFresh(nextDeck);

      const responseMap = {};
      for (const r of payload.card_responses || []) {
        if (r && r.card_uuid) responseMap[r.card_uuid] = r;
      }
      setResponsesByCardUuid(responseMap);

      setCompletion(payload.deck_completion || null);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [
    deckUuid,
    sectionUuid,
    companyId,
    employeeID,
    language,
    previewMode,
    isPublic,
    previewDeck,
    markDeckMediaUrlsFresh,
  ]);

  React.useEffect(() => {
    fetchDeck();
  }, [fetchDeck]);

  const cardHasMedia = React.useCallback(
    (cardUuid) => {
      const card = (deck?.cards || []).find((c) => c.uuid === cardUuid);
      return !!(card?.media?.uuid || card?.thumbnail?.uuid);
    },
    [deck],
  );

  const isCardMediaStale = React.useCallback(
    (cardUuid) => {
      // No media refresh in preview or public mode (no companyId / no auth to
      // re-sign with; public deck URLs are session-valid).
      if (nonTracking || !cardHasMedia(cardUuid)) return false;
      const fetchedAt = mediaUrlFetchedAtByCardRef.current[cardUuid];
      if (!fetchedAt) return true;
      return Date.now() - fetchedAt >= SIGNED_MEDIA_REFRESH_AFTER_MS;
    },
    [nonTracking, cardHasMedia],
  );

  const fetchLearningFileUrl = React.useCallback(
    async (fileUuid) => {
      if (!fileUuid || !companyId) return null;
      const res = await getRightworkAPI(API_get_learning_file, {
        company_id: companyId,
        learning_file_uuid: fileUuid,
      });
      return res?.data?.redux?.payload?.learning_file?.file || null;
    },
    [companyId],
  );

  const refreshCardMedia = React.useCallback(
    async (cardUuid) => {
      if (nonTracking || !companyId || !isCardMediaStale(cardUuid)) return;
      if (mediaRefreshInFlightByCardRef.current[cardUuid]) {
        return mediaRefreshInFlightByCardRef.current[cardUuid];
      }

      const card = (deck?.cards || []).find((c) => c.uuid === cardUuid);
      const mediaUuid = card?.media?.uuid || null;
      const thumbnailUuid = card?.thumbnail?.uuid || null;
      if (!mediaUuid && !thumbnailUuid) return;

      const refreshPromise = Promise.all([
        fetchLearningFileUrl(mediaUuid),
        fetchLearningFileUrl(thumbnailUuid),
      ])
        .then(([mediaUrl, thumbnailUrl]) => {
          setDeck((prevDeck) => {
            if (!prevDeck) return prevDeck;
            return {
              ...prevDeck,
              cards: (prevDeck.cards || []).map((prevCard) => {
                if (prevCard.uuid !== cardUuid) return prevCard;
                return {
                  ...prevCard,
                  media:
                    mediaUrl && prevCard.media
                      ? { ...prevCard.media, url: mediaUrl }
                      : prevCard.media,
                  thumbnail:
                    thumbnailUrl && prevCard.thumbnail
                      ? { ...prevCard.thumbnail, url: thumbnailUrl }
                      : prevCard.thumbnail,
                };
              }),
            };
          });
          mediaUrlFetchedAtByCardRef.current[cardUuid] = Date.now();
        })
        .finally(() => {
          delete mediaRefreshInFlightByCardRef.current[cardUuid];
        });

      mediaRefreshInFlightByCardRef.current[cardUuid] = refreshPromise;
      return refreshPromise;
    },
    [
      companyId,
      deck,
      fetchLearningFileUrl,
      isCardMediaStale,
      nonTracking,
    ],
  );

  /**
   * Submit a poll or quiz answer. Idempotent on the server (update_or_create
   * keyed by card+user+section), so re-answering replaces the prior row.
   * Optimistically updates the response map on success so the UI can show
   * "you already answered this" immediately.
   */
  const submitResponse = React.useCallback(
    async (cardUuid, selectedIndices) => {
      // Public mode: post to the unauthenticated submit endpoint (bare axios,
      // no token). Polls return aggregate vote_counts; quizzes are graded
      // server-side and return is_correct / correct_indices / explanation.
      // No per-user record is written.
      if (isPublic) {
        const res = await axios.post(API_post_public_swipe_card_submit, {
          share_token: publicShareToken,
          learning_swipe_card_uuid: cardUuid,
          selected_indices: selectedIndices,
          language,
        });
        const payload = res.data || {};
        const response = {
          uuid: `public-response-${cardUuid}`,
          card_uuid: cardUuid,
          section_uuid: null,
          selected_indices: selectedIndices,
          is_correct: payload.is_correct ?? null,
          vote_counts: payload.vote_counts ?? null,
          correct_indices: payload.correct_indices ?? null,
          explanation: payload.explanation ?? null,
        };
        setResponsesByCardUuid((prev) => ({ ...prev, [cardUuid]: response }));
        return response;
      }

      // Preview mode: compute is_correct locally from the answer keys the
      // admin endpoint returned. No server call, no response history.
      if (previewMode) {
        const card = (deck?.cards || []).find((c) => c.uuid === cardUuid);
        let isCorrect = null;
        if (card?.card_type === "quiz") {
          const choices = card?.content?.choices || [];
          const correctIdx = new Set(
            choices
              .map((c, i) => (c && c.is_correct ? i : null))
              .filter((i) => i !== null),
          );
          const selected = new Set(selectedIndices);
          isCorrect =
            correctIdx.size === selected.size &&
            [...correctIdx].every((i) => selected.has(i));
        }
        // Synthesize vote_counts for poll previews so admins can see the
        // percentage-bar UI while authoring. Treat the admin's own pick as
        // the only vote — produces a 100% bar on the chosen option, which
        // is the right preview semantic ("here's what your team will see
        // post-vote") even though it's a degenerate distribution.
        let voteCounts = null;
        if (card?.card_type === "poll") {
          voteCounts = {};
          for (const idx of selectedIndices) {
            voteCounts[String(idx)] = 1;
          }
        }
        // Synthesize the post-submit reveal fields (correct_indices,
        // explanation) for quiz previews so admins can see what
        // employees will see after submitting. In preview mode the BE
        // never strips answer keys, so card.content already has both
        // available — we just shape them like the BE response payload.
        let correctIndices = null;
        let explanationText = null;
        if (card?.card_type === "quiz") {
          const choices = card?.content?.choices || [];
          correctIndices = choices
            .map((c, i) => (c && c.is_correct ? i : null))
            .filter((i) => i !== null);
          explanationText = card?.content?.explanation || null;
        }
        const fakeResponse = {
          uuid: `preview-response-${cardUuid}`,
          card_uuid: cardUuid,
          section_uuid: null,
          selected_indices: selectedIndices,
          is_correct: isCorrect,
          vote_counts: voteCounts,
          correct_indices: correctIndices,
          explanation: explanationText,
        };
        setResponsesByCardUuid((prev) => ({
          ...prev,
          [cardUuid]: fakeResponse,
        }));
        return fakeResponse;
      }

      const data = {
        company_id: companyId,
        learning_swipe_card_uuid: cardUuid,
        section_uuid: sectionUuid,
        // Server expects a JSON-stringified array (the endpoint also
        // accepts a raw array, but stringifying matches the shape used
        // by post_learning_block_user_passed for question_responses).
        selected_indices: JSON.stringify(selectedIndices),
      };
      if (employeeID) data.store_team_member_id = employeeID;
      // Pass language so the BE response payload's `explanation` field
      // is localized to the player's current language.
      if (language) data.language = language;

      const res = await postRightworkAPI(
        API_post_learning_swipe_card_response,
        data,
      );
      const cardResponse = res.data?.redux?.payload?.card_response;
      if (cardResponse && cardResponse.card_uuid) {
        setResponsesByCardUuid((prev) => ({
          ...prev,
          [cardResponse.card_uuid]: cardResponse,
        }));
      }
      return cardResponse;
    },
    [companyId, sectionUuid, employeeID, language, previewMode, isPublic, publicShareToken, deck],
  );

  /**
   * Mark the deck complete. No-op if already complete (server is idempotent
   * via get_or_create, but we short-circuit locally to avoid a wasted call).
   *
   * Server-side validation requires every interactive card in the deck to
   * have a response — the player should only call this after confirming
   * that, but the BE will reject otherwise.
   */
  const submitCompletion = React.useCallback(async () => {
    if (completion) return completion;

    // Preview / public mode: stub the completion record locally. No server
    // write — public links are deliberately untracked, and preview must not
    // pollute the admin's completion history.
    if (nonTracking) {
      const elapsedSeconds = Math.max(
        0,
        Math.floor((Date.now() - startedAtRef.current) / 1000),
      );
      const fakeCompletion = {
        uuid: "preview-completion",
        deck_uuid: deck?.uuid || deckUuid || "preview-deck",
        section_uuid: null,
        time_spent_seconds: elapsedSeconds,
      };
      setCompletion(fakeCompletion);
      return fakeCompletion;
    }

    const elapsedSeconds = Math.max(
      0,
      Math.floor((Date.now() - startedAtRef.current) / 1000),
    );
    const data = {
      company_id: companyId,
      learning_swipe_deck_uuid: deckUuid,
      section_uuid: sectionUuid,
      time_spent_seconds: elapsedSeconds,
    };
    if (employeeID) data.store_team_member_id = employeeID;

    const res = await postRightworkAPI(
      API_post_learning_swipe_deck_completion,
      data,
    );
    const newCompletion = res.data?.redux?.payload?.deck_completion;
    if (newCompletion) setCompletion(newCompletion);
    return newCompletion;
  }, [
    companyId,
    deckUuid,
    sectionUuid,
    employeeID,
    completion,
    nonTracking,
    deck,
  ]);

  return {
    deck,
    responsesByCardUuid,
    completion,
    isLoading,
    error,
    submitResponse,
    submitCompletion,
    refreshCardMedia,
    isCardMediaStale,
    refetch: fetchDeck,
  };
}
